/**
 * Ad Display Components for showing advertisements to students
 */

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Track ad impression
const trackImpression = async (adId) => {
  try {
    await fetch(`${API_URL}/api/ad-tracking/impression`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ advertisement_id: adId }),
    });
  } catch (e) {
    console.error('Failed to track impression:', e);
  }
};

// Track ad click
const trackClick = async (adId) => {
  try {
    await fetch(`${API_URL}/api/ad-tracking/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ advertisement_id: adId }),
    });
  } catch (e) {
    console.error('Failed to track click:', e);
  }
};

// Banner Ad Component - Supports image and video
export const AdBanner = ({ placement, className }) => {
  const [ad, setAd] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const url = `${API_URL}/api/ads/serve?placement=${placement}`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          if (data.ad) {
            setAd(data.ad);
            // Track impression when ad loads
            trackImpression(data.ad.id);
          }
        }
      } catch (e) {
        console.error('AdBanner: Failed to fetch ad:', e);
      } finally {
        setLoaded(true);
      }
    };
    fetchAd();
  }, [placement]);

  const handleClick = () => {
    if (ad?.click_url) {
      trackClick(ad.id);
      window.open(ad.click_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Need either image or video
  if (!ad || !loaded || (!ad.image_url && !ad.video_url)) return null;

  return (
    <div 
      className={cn(
        "overflow-hidden rounded-xl",
        ad.click_url && "cursor-pointer",
        className
      )}
      onClick={handleClick}
      data-testid="ad-banner"
    >
      {ad.video_url ? (
        <video 
          src={ad.video_url}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-auto"
        />
      ) : (
        <img 
          src={ad.image_url} 
          alt={ad.title || "Advertisement"} 
          className="w-full h-auto object-contain"
        />
      )}
    </div>
  );
};

// Compact Ad Card (for sidebar or inline) - Supports image and video
export const AdCard = ({ placement, className }) => {
  const [ad, setAd] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ads/serve?placement=${placement}`);
        if (res.ok) {
          const data = await res.json();
          if (data.ad) {
            setAd(data.ad);
            trackImpression(data.ad.id);
          }
        }
      } catch (e) {
        console.error('Failed to fetch ad:', e);
      } finally {
        setLoaded(true);
      }
    };
    fetchAd();
  }, [placement]);

  const handleClick = () => {
    if (ad?.click_url) {
      trackClick(ad.id);
      window.open(ad.click_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Need either image or video
  if (!ad || !loaded || (!ad.image_url && !ad.video_url)) return null;

  return (
    <div 
      className={cn(
        "rounded-xl overflow-hidden",
        ad.click_url && "cursor-pointer",
        className
      )}
      onClick={handleClick}
      data-testid="ad-card"
    >
      {ad.video_url ? (
        <video 
          src={ad.video_url}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-auto"
        />
      ) : (
        <img 
          src={ad.image_url} 
          alt={ad.title || "Advertisement"} 
          className="w-full h-auto object-contain"
        />
      )}
    </div>
  );
};

export default AdBanner;
